import React, { useState, useEffect, useCallback } from 'react';
import './StudentCertificates.css';
import collegeConfig from '../../config/collegeConfig';
import { jsPDF } from 'jspdf';
import { generateClassicDRPCertificate } from '../../utils/classicDrpCertificateGenerator';
import { savePDF } from '../../utils/savePDF';
import { saveFile } from '../../utils/saveFile';
import CertUploadWidget from '../shared/CertUploadWidget';
import {
  FaAward,
  FaDownload,
  FaHospital,
  FaSpinner,
  FaArrowLeft,
  FaCheckCircle,
  FaTimesCircle,
  FaExternalLinkAlt,
  FaUpload,
  FaHistory,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaUserMd,
  FaChevronDown,
  FaChevronUp,
  FaFilePdf,
  FaFileImage,
  FaTrash
} from 'react-icons/fa';

const API = collegeConfig.api.baseURL;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
};

const formatDateShort = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isCompleted = (toDate) => {
  if (!toDate) return false;
  const t = new Date(toDate); t.setHours(0, 0, 0, 0);
  const n = new Date(); n.setHours(0, 0, 0, 0);
  return t < n;
};

const getBase64Image = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

// ─── PDF Generator ────────────────────────────────────────────────────────────
const downloadCertificatePDF = async (cert, collegeName, logoSrc) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  const MAROON   = [142, 44,  44];
  const GOLD     = [212, 177, 90];
  const NAVY     = [31,  60,  136];
  const DARKGRAY = [60,  60,  60];

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(3);
  doc.rect(8, 8, W - 16, H - 16);
  doc.setLineWidth(1);
  doc.rect(12, 12, W - 24, H - 24);

  doc.setFillColor(...MAROON);
  doc.rect(12, 12, W - 24, 52, 'F');

  const logoBase64 = await getBase64Image(logoSrc);
  if (logoBase64) {
    try { doc.addImage(logoBase64, 'PNG', W / 2 - 14, 13, 28, 28); }
    catch (_) { /* skip */ }
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  const nameLines = doc.splitTextToSize(collegeName, W - 60);
  doc.text(nameLines, W / 2, 48, { align: 'center' });

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(1.2);
  doc.line(15, 66, W - 15, 66);

  doc.setTextColor(...MAROON);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.text('CERTIFICATE', W / 2, 82, { align: 'center' });

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(12);
  doc.setTextColor(...GOLD);
  doc.text('of Course Completion', W / 2, 90, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...NAVY);
  doc.text(cert.courseName || '', W / 2, 99, { align: 'center' });

  const startYear  = cert.batchStartYear || '____';
  const endYear    = cert.batchEndYear   || '____';
  const level      = cert.courseLevel    || 'MD/MS';
  const courseName = cert.courseName     || '';

  const bodyText =
    `This is to certify that DR. ${cert.studentName} has successfully completed postgraduate training ` +
    `from ${startYear} to ${endYear} for ${level} in ${courseName} from ${collegeName}. ` +
    `The work and academic activities recorded in the Logbook are as per hospital records and have been ` +
    `carried out under guidance of faculty members of the Department of ${courseName}.`;

  doc.setFont('times', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...DARKGRAY);
  const bodyLines = doc.splitTextToSize(bodyText, W - 60);
  doc.text(bodyLines, W / 2, 111, { align: 'center', lineHeightFactor: 1.6 });

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.8);
  doc.line(15, H - 52, W - 15, H - 52);

  const col1 = 50;
  const col2 = W / 2;
  const col3 = W - 50;
  const sigY = H - 32;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);
  doc.text('Guide', col1, sigY - 12, { align: 'center' });
  doc.setDrawColor(...DARKGRAY);
  doc.setLineWidth(0.4);
  doc.line(col1 - 30, sigY - 4, col1 + 30, sigY - 4);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARKGRAY);
  doc.text(cert.guideName || '', col1, sigY, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);
  doc.text('Date', col2, sigY - 12, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARKGRAY);
  doc.text(formatDate(cert.generatedAt), col2, sigY, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);
  doc.text('Head of Department', col3, sigY - 12, { align: 'center' });
  doc.setDrawColor(...DARKGRAY);
  doc.setLineWidth(0.4);
  doc.line(col3 - 35, sigY - 4, col3 + 35, sigY - 4);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARKGRAY);
  doc.text(cert.hodName || '', col3, sigY, { align: 'center' });

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text(`Certificate No: ${cert.certificateNumber || ''}`, W / 2, H - 16, { align: 'center' });

  await savePDF(doc, `Course_Completion_Certificate.pdf`);
};

// ─── Single past-DRP row in the history list ──────────────────────────────────
const HistoryDRPRow = ({ hist, index, onUpload, uploading, onDownloadSystem, onDownloadUploaded, downloading, onDelete, deleting }) => {
  const [expanded, setExpanded] = useState(false);
  const hasCert = hist.certificate?.issued || hist.uploadedCertificate?.fileName;
  const noIncharge = !hist.profile?.inchargeId && !hist.profile?.inchargeName;
  const needsUpload = !hasCert;

  return (
    <div style={{ ...s.histRow, border: needsUpload ? '1px solid #fca5a5' : '1px solid #e5e7eb' }}>
      {/* Index badge */}
      <div style={{ ...s.histIndex, background: needsUpload ? '#fee2e2' : '#d1fae5', color: needsUpload ? '#b91c1c' : '#065f46' }}>
        DRP {index}
      </div>

      <div style={{ flex: 1 }}>
        {/* Header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>{hist.profile?.hospitalName || '—'}</div>
            <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
              <FaCalendarAlt size={10} style={{ marginRight: 4 }} />
              {formatDateShort(hist.profile?.fromDate)} – {formatDateShort(hist.profile?.toDate)}
            </div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
              <FaUserMd size={10} style={{ marginRight: 4 }} />
              {hist.profile?.inchargeName || (hist.profile?.inchargeId ? 'Incharge assigned' : <em>No Incharge</em>)}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
            {hist.certificate?.issued && (
              <span style={s.badgeSystem}><FaAward size={9} style={{ marginRight: 3 }} />System Cert</span>
            )}
            {hist.uploadedCertificate?.fileName && (
              <span style={s.badgeUploaded}><FaUpload size={9} style={{ marginRight: 3 }} />Uploaded Cert</span>
            )}
            {needsUpload && (
              <span style={s.badgeNone}><FaExclamationTriangle size={9} style={{ marginRight: 3 }} />No Certificate</span>
            )}
          </div>
        </div>

        {/* Warning if no incharge and no cert */}
        {noIncharge && needsUpload && (
          <div style={{ marginTop: 8, padding: '8px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
            <FaExclamationTriangle style={{ flexShrink: 0, marginTop: 1 }} />
            No DRP Incharge was assigned. Upload your certificate manually to mark this DRP complete.
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {hist.certificate?.issued && (
            <button
              onClick={() => onDownloadSystem(hist)}
              disabled={downloading === hist._id}
              style={s.actionBtn}
            >
              {downloading === hist._id
                ? <FaSpinner className="stucert-spinner-sm" style={{ marginRight: 6 }} />
                : <FaDownload style={{ marginRight: 6 }} />}
              Download System Certificate
            </button>
          )}
          {hist.uploadedCertificate?.fileName && (
            <>
              <button
                onClick={() => onDownloadUploaded(hist._id)}
                disabled={downloading === hist._id || deleting === hist._id}
                style={{ ...s.actionBtn, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}
              >
                {downloading === hist._id
                  ? <FaSpinner className="stucert-spinner-sm" style={{ marginRight: 6 }} />
                  : <FaDownload style={{ marginRight: 6 }} />}
                Download Uploaded Certificate
              </button>
              <button
                onClick={() => onDelete(hist._id)}
                disabled={deleting === hist._id || downloading === hist._id}
                style={{ ...s.actionBtn, background: '#fff5f5', color: '#dc2626', border: '1px solid #fecaca' }}
              >
                {deleting === hist._id
                  ? <FaSpinner className="stucert-spinner-sm" style={{ marginRight: 6 }} />
                  : <FaTrash style={{ marginRight: 6 }} />}
                Delete
              </button>
            </>
          )}
          <button onClick={() => setExpanded(x => !x)} style={s.expandBtn}>
            {expanded ? <FaChevronUp size={10} style={{ marginRight: 4 }} /> : <FaChevronDown size={10} style={{ marginRight: 4 }} />}
            {expanded ? 'Hide details' : 'View details'}
          </button>
        </div>

        {/* Upload widget when no cert */}
        {needsUpload && (
          <CertUploadWidget
            inputId={`cert-hist-${hist._id}`}
            uploading={uploading === hist._id}
            onUpload={(payload) => onUpload(hist._id, payload)}
          />
        )}

        {/* Expanded details */}
        {expanded && (
          <div style={{ marginTop: 12, borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12 }}>
              <div><span style={{ color: '#888' }}>Attendance: </span><strong>{hist.attendance?.length || 0} days</strong></div>
              <div><span style={{ color: '#888' }}>Diary entries: </span><strong>{hist.diary?.length || 0}</strong></div>
              <div><span style={{ color: '#888' }}>Operations: </span><strong>{hist.operations?.length || 0}</strong></div>
              <div><span style={{ color: '#888' }}>Public Health: </span><strong>{hist.publicHealth?.length || 0}</strong></div>
            </div>
            {hist.inchargeVerification?.remarks && (
              <div style={{ marginTop: 8, padding: '8px 10px', background: '#f8f8f8', borderRadius: 8, borderLeft: '3px solid #0f766e', fontSize: 12 }}>
                <strong>Incharge Remarks:</strong> {hist.inchargeVerification.remarks}
              </div>
            )}
            {hist.certificate?.issuedAt && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#059669' }}>
                System certificate issued: {formatDateShort(hist.certificate.issuedAt)}
              </div>
            )}
            {hist.uploadedCertificate?.uploadedAt && (
              <div style={{ marginTop: 4, fontSize: 12, color: '#2563eb' }}>
                Certificate uploaded: {formatDateShort(hist.uploadedCertificate.uploadedAt)}
                {hist.uploadedCertificate.fileName && <span style={{ color: '#666' }}> ({hist.uploadedCertificate.fileName})</span>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Certification type config ────────────────────────────────────────────────
const CERT_CONFIG = [
  {
    key: 'researchMethodology',
    label: 'Research Methodology Course Certificate',
    shortLabel: 'Research Methodology',
    icon: '🔬',
    color: '#7c3aed',
    bg: '#f5f3ff',
    border: '#ddd6fe',
    desc: 'Certificate of completion for the Research Methodology course.'
  },
  {
    key: 'ethicsCourse',
    label: 'Ethics Course Certificate',
    shortLabel: 'Ethics Course',
    icon: '⚖️',
    color: '#0369a1',
    bg: '#f0f9ff',
    border: '#bae6fd',
    desc: 'Certificate of completion for the Medical Ethics course.'
  },
  {
    key: 'bclsAcls',
    label: 'BCLS & ACLS Certificate',
    shortLabel: 'BCLS & ACLS',
    icon: '❤️',
    color: '#b91c1c',
    bg: '#fff1f2',
    border: '#fecdd3',
    desc: 'Basic & Advanced Cardiac Life Support (BCLS/ACLS) certification.'
  },
  {
    key: 'posterPresentation',
    label: 'Poster / Paper Presentation Certificate',
    shortLabel: 'Poster/Paper Presentation',
    icon: '🗂️',
    color: '#0f766e',
    bg: '#f0fdfa',
    border: '#99f6e4',
    desc: 'Certificate for Poster or Paper Presentation at a conference or academic event.'
  }
];

// ─── Component ────────────────────────────────────────────────────────────────
const StudentCertificates = ({ onNavigate }) => {
  const [courseCompletionCert, setCourseCompletionCert] = useState(null);
  const [drpPostings, setDrpPostings] = useState([]);
  const [classicDRPEntry, setClassicDRPEntry] = useState(null);
  const [drpMode, setDrpMode] = useState('advanced');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingClassicDRP, setDownloadingClassicDRP] = useState(false);
  const [certUploading, setCertUploading] = useState(null);
  const [certDownloading, setCertDownloading] = useState(null);
  const [toast, setToast] = useState('');

  // Uploaded certifications state
  const [certifications, setCertifications] = useState({
    researchMethodology: null,
    ethicsCourse: null,
    bclsAcls: null,
    posterPresentation: null
  });
  const [certUploadingType, setCertUploadingType] = useState(null);
  const [certDownloadingType, setCertDownloadingType] = useState(null);
  const [certDeletingType, setCertDeletingType] = useState(null);
  const [drpCertDeleting, setDrpCertDeleting] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const fetchCertificates = useCallback(async () => {
    setLoading(true);
    try {
      const [certRes, drpRes, classicRes, myCardsRes] = await Promise.all([
        fetch(`${API}/certificates/my-certificate`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/drp/my-postings`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/classic-drp/my-entry`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/certificates/my-certifications`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (certRes.ok) {
        const certData = await certRes.json();
        setCourseCompletionCert(certData.certificate || null);
      }
      if (drpRes.ok) {
        const drpData = await drpRes.json();
        setDrpPostings((drpData.postings || []).filter(p => p.certificateUrl));
      }
      if (classicRes.ok) {
        const classicData = await classicRes.json();
        if (classicData.success) setClassicDRPEntry(classicData.entry);
      }
      if (myCardsRes.ok) {
        const myCardsData = await myCardsRes.json();
        if (myCardsData.success) setCertifications(myCardsData.certifications);
      }
    } catch (err) {
      console.error('Error fetching certificates:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchCertificates(); }, [fetchCertificates]);

  useEffect(() => {
    fetch(`${API}/system-settings`)
      .then(r => r.json())
      .then(d => { if (d.success) setDrpMode(d.drpMode || 'advanced'); })
      .catch(() => {});
  }, []);

  const handleDownloadCourseCompletion = async () => {
    if (!courseCompletionCert) return;
    setDownloading(true);
    try { await downloadCertificatePDF(courseCompletionCert, collegeConfig.collegeName, collegeConfig.logo); }
    finally { setDownloading(false); }
  };

  const handleDownloadDRP = (posting) => {
    window.open(`${API}${posting.certificateUrl}`, '_blank');
  };

  // Download system-generated Classic DRP PDF (current or history profile)
  const downloadSystemCert = async (profile, isCurrent = false) => {
    if (isCurrent) setDownloadingClassicDRP(true);
    try {
      const me = JSON.parse(localStorage.getItem('user') || '{}');
      const studentName = `${me.profile?.firstName || ''} ${me.profile?.lastName || ''}`.trim();
      const courseName = me.assignedCourses?.[0]?.courseName || '';
      const inchargeData = profile?.inchargeId;
      const inchargeName = profile?.inchargeName ||
        (inchargeData ? `${inchargeData.profile?.firstName || ''} ${inchargeData.profile?.lastName || ''}`.trim() : '');
      await generateClassicDRPCertificate(
        { studentName, courseName, hospitalName: profile?.hospitalName, fromDate: profile?.fromDate, toDate: profile?.toDate, inchargeName },
        collegeConfig.collegeName, collegeConfig.logo
      );
    } finally {
      if (isCurrent) setDownloadingClassicDRP(false);
    }
  };

  // Download an uploaded certificate from the server
  const downloadUploadedCert = async (histId) => {
    const path = histId === 'current' ? '/classic-drp/certificate' : `/classic-drp/history/${histId}/certificate`;
    setCertDownloading(histId);
    try {
      const r = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) {
        await saveFile(d.data, d.mimeType, d.fileName || 'certificate');
      } else showToast('Download failed');
    } catch { showToast('Download failed'); }
    setCertDownloading(null);
  };

  // Upload a certificate (current DRP or history item)
  const uploadCertificate = async (histId, payload) => {
    const path = histId === 'current' ? '/classic-drp/upload-certificate' : `/classic-drp/history/${histId}/upload-certificate`;
    setCertUploading(histId);
    try {
      const r = await fetch(`${API}${path}`, { method: 'POST', headers, body: JSON.stringify(payload) });
      const d = await r.json();
      if (d.success) { showToast('Certificate uploaded successfully'); fetchCertificates(); }
      else showToast(d.message || 'Upload failed');
    } catch { showToast('Upload failed'); }
    setCertUploading(null);
  };

  // Upload one of the 3 certification types
  const uploadCertType = async (type, payload) => {
    setCertUploadingType(type);
    try {
      const r = await fetch(`${API}/certificates/upload/${type}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      const d = await r.json();
      if (d.success) {
        showToast('Certificate uploaded successfully');
        fetchCertificates();
      } else {
        showToast(d.message || 'Upload failed');
      }
    } catch {
      showToast('Upload failed');
    }
    setCertUploadingType(null);
  };

  // Download one of the 3 certification types
  const downloadCertType = async (type) => {
    setCertDownloadingType(type);
    try {
      const r = await fetch(`${API}/certificates/download/${type}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (d.success) {
        await saveFile(d.data, d.mimeType, d.fileName || 'certificate');
      } else {
        showToast('Download failed');
      }
    } catch {
      showToast('Download failed');
    }
    setCertDownloadingType(null);
  };

  // Delete one of the 3 certification types
  const deleteCertType = async (type, label) => {
    if (!window.confirm(`Delete your uploaded ${label} certificate? This cannot be undone.`)) return;
    setCertDeletingType(type);
    try {
      const r = await fetch(`${API}/certificates/delete/${type}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (d.success) { showToast('Certificate deleted'); fetchCertificates(); }
      else showToast(d.message || 'Delete failed');
    } catch { showToast('Delete failed'); }
    setCertDeletingType(null);
  };

  // Delete uploaded DRP certificate (current or history)
  const deleteDrpCert = async (histId) => {
    if (!window.confirm('Delete this uploaded DRP certificate? This cannot be undone.')) return;
    const path = histId === 'current' ? '/classic-drp/certificate' : `/classic-drp/history/${histId}/certificate`;
    setDrpCertDeleting(histId);
    try {
      const r = await fetch(`${API}${path}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (d.success) { showToast('Certificate deleted'); fetchCertificates(); }
      else showToast(d.message || 'Delete failed');
    } catch { showToast('Delete failed'); }
    setDrpCertDeleting(null);
  };

  if (loading) {
    return (
      <div className="stucert-loading">
        <FaSpinner className="stucert-spinner" />
        <p>Loading certificates...</p>
      </div>
    );
  }

  const drpHistory = classicDRPEntry?.drpHistory || [];
  const currentCompleted = isCompleted(classicDRPEntry?.profile?.toDate);
  const hasIncharge = !!(classicDRPEntry?.profile?.inchargeId);
  const currentHasCert = !!(classicDRPEntry?.certificate?.issued || classicDRPEntry?.uploadedCertificate?.fileName);
  const noInchargeAndCompleted = currentCompleted && !hasIncharge && !currentHasCert;
  const totalDRPs = drpHistory.length + (classicDRPEntry?.profile?.hospitalName ? 1 : 0);

  return (
    <div className="stucert-container">
      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#1a1a2e', color: '#fff', padding: '10px 20px', borderRadius: 20, fontSize: 14, zIndex: 2000, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="stucert-header">
        <button className="stucert-back-btn" onClick={() => onNavigate('dashboard')}>
          <FaArrowLeft /> Back
        </button>
        <div className="stucert-header-title">
          <FaAward className="stucert-header-icon" />
          <h2>My Certificates</h2>
        </div>
      </div>

      {/* ── Course Completion Certificate ──────────────────────────────────── */}
      <section className="stucert-section">
        <div className="stucert-section-header">
          <FaAward className="stucert-section-icon maroon" />
          <h3>Course Completion Certificate</h3>
        </div>

        {courseCompletionCert ? (
          <div className="stucert-card stucert-card-issued">
            <div className="stucert-card-status issued">
              <FaCheckCircle /> Certificate Issued
            </div>
            <div className="stucert-cert-preview">
              <div className="stucert-preview-header">
                <img src={collegeConfig.logo} alt="College Logo" className="stucert-preview-logo" />
                <div>
                  <p className="stucert-preview-college">{collegeConfig.collegeName}</p>
                  <p className="stucert-preview-cert-label">CERTIFICATE of Course Completion</p>
                </div>
              </div>
              <p className="stucert-preview-course">{courseCompletionCert.courseName}</p>
              <p className="stucert-preview-body">
                This is to certify that <strong>DR. {courseCompletionCert.studentName}</strong> has
                successfully completed postgraduate training
                {courseCompletionCert.batchStartYear && courseCompletionCert.batchEndYear
                  ? ` from ${courseCompletionCert.batchStartYear} to ${courseCompletionCert.batchEndYear}`
                  : ''} for {courseCompletionCert.courseLevel || 'MD/MS'} in {courseCompletionCert.courseName} from {collegeConfig.collegeName}.
              </p>
              <div className="stucert-preview-meta">
                {courseCompletionCert.guideName && (
                  <span>Guide: <strong>{courseCompletionCert.guideName}</strong></span>
                )}
                <span>HOD: <strong>{courseCompletionCert.hodName}</strong></span>
                <span>Date: <strong>{formatDate(courseCompletionCert.generatedAt)}</strong></span>
              </div>
              <p className="stucert-cert-number">Certificate No: {courseCompletionCert.certificateNumber}</p>
            </div>
            <button className="stucert-download-btn" onClick={handleDownloadCourseCompletion} disabled={downloading}>
              {downloading ? <><FaSpinner className="stucert-spinner-sm" /> Generating PDF...</> : <><FaDownload /> Download as PDF</>}
            </button>
          </div>
        ) : (
          <div className="stucert-card stucert-card-pending">
            <div className="stucert-card-status pending">
              <FaTimesCircle /> Not Yet Issued
            </div>
            <p className="stucert-pending-msg">
              Your course completion certificate has not been generated yet. It will be issued by the Head of Department upon completing your postgraduate course.
            </p>
          </div>
        )}
      </section>

      {/* ── DRP Certificate section ──────────────────────────────────────────── */}
      {drpMode === 'classic' ? (
        /* ── Classic DRP ── */
        classicDRPEntry && classicDRPEntry.profile?.hospitalName && (
          <section className="stucert-section">
            <div className="stucert-section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaHospital className="stucert-section-icon blue" />
                <h3 style={{ margin: 0 }}>DRP Certificate{totalDRPs > 1 ? 's' : ''}</h3>
              </div>
              {totalDRPs > 1 && (
                <span style={{ fontSize: 12, background: '#ede9fe', color: '#7c3aed', padding: '3px 10px', borderRadius: 12, fontWeight: 700 }}>
                  {totalDRPs} DRPs
                </span>
              )}
            </div>

            {/* ── Current DRP ── */}
            {drpHistory.length > 0 && (
              <div style={s.subLabel}>
                <FaHospital size={11} style={{ marginRight: 5 }} />
                Current DRP — {classicDRPEntry.profile.hospitalName}
              </div>
            )}

            {/* System cert issued */}
            {classicDRPEntry.certificate?.issued ? (
              <div className="stucert-card stucert-card-issued">
                <div className="stucert-card-status issued">
                  <FaCheckCircle /> DRP Certificate Issued
                </div>
                <div className="stucert-drp-info">
                  <p><strong>Hospital:</strong> {classicDRPEntry.profile.hospitalName}</p>
                  <p><strong>Period:</strong> {formatDate(classicDRPEntry.profile.fromDate)} – {formatDate(classicDRPEntry.profile.toDate)}</p>
                  {classicDRPEntry.certificate.issuedAt && (
                    <p><strong>Issued On:</strong> {formatDate(classicDRPEntry.certificate.issuedAt)}</p>
                  )}
                </div>
                <button
                  className="stucert-download-btn stucert-download-btn-blue"
                  onClick={() => downloadSystemCert(classicDRPEntry.profile, true)}
                  disabled={downloadingClassicDRP}
                >
                  {downloadingClassicDRP
                    ? <><FaSpinner className="stucert-spinner-sm" /> Generating PDF...</>
                    : <><FaDownload /> Download DRP Certificate</>}
                </button>
              </div>

            /* Uploaded cert exists */
            ) : classicDRPEntry.uploadedCertificate?.fileName ? (
              <div className="stucert-card stucert-card-issued">
                <div className="stucert-card-status issued">
                  <FaCheckCircle /> Certificate Uploaded
                </div>
                <div className="stucert-drp-info">
                  <p><strong>Hospital:</strong> {classicDRPEntry.profile.hospitalName}</p>
                  <p><strong>Period:</strong> {formatDate(classicDRPEntry.profile.fromDate)} – {formatDate(classicDRPEntry.profile.toDate)}</p>
                  <p><strong>File:</strong> {classicDRPEntry.uploadedCertificate.fileName}</p>
                  <p><strong>Uploaded:</strong> {formatDate(classicDRPEntry.uploadedCertificate.uploadedAt)}</p>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    className="stucert-download-btn stucert-download-btn-blue"
                    onClick={() => downloadUploadedCert('current')}
                    disabled={certDownloading === 'current' || drpCertDeleting === 'current'}
                  >
                    {certDownloading === 'current'
                      ? <><FaSpinner className="stucert-spinner-sm" /> Downloading...</>
                      : <><FaDownload /> Download Uploaded Certificate</>}
                  </button>
                  <button
                    onClick={() => deleteDrpCert('current')}
                    disabled={drpCertDeleting === 'current' || certDownloading === 'current'}
                    style={{ ...s.actionBtn, background: '#fff5f5', color: '#dc2626', border: '1px solid #fecaca' }}
                  >
                    {drpCertDeleting === 'current'
                      ? <FaSpinner className="stucert-spinner-sm" style={{ marginRight: 6 }} />
                      : <FaTrash style={{ marginRight: 6 }} />}
                    Delete
                  </button>
                </div>
              </div>

            /* No incharge + completed = must upload */
            ) : noInchargeAndCompleted ? (
              <div className="stucert-card" style={{ border: '1px solid #fed7aa', background: '#fffbeb' }}>
                <div className="stucert-card-status" style={{ color: '#92400e', background: '#fef3c7', borderRadius: 8, padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                  <FaExclamationTriangle /> Upload Required
                </div>
                <div className="stucert-drp-info">
                  <p><strong>Hospital:</strong> {classicDRPEntry.profile.hospitalName}</p>
                  <p><strong>Period:</strong> {formatDate(classicDRPEntry.profile.fromDate)} – {formatDate(classicDRPEntry.profile.toDate)}</p>
                </div>
                <p style={{ fontSize: 13, color: '#92400e', margin: '8px 0' }}>
                  No DRP Incharge was assigned for this posting. Upload your certificate manually to complete this DRP.
                </p>
                <CertUploadWidget
                  inputId="cert-current-main"
                  uploading={certUploading === 'current'}
                  onUpload={(payload) => uploadCertificate('current', payload)}
                />
              </div>

            /* Has incharge, waiting */
            ) : currentCompleted ? (
              <div className="stucert-card stucert-card-pending">
                <div className="stucert-card-status pending">
                  <FaTimesCircle /> Certificate Not Yet Issued
                </div>
                <div className="stucert-drp-info">
                  <p><strong>Hospital:</strong> {classicDRPEntry.profile.hospitalName}</p>
                  <p><strong>Period:</strong> {formatDate(classicDRPEntry.profile.fromDate)} – {formatDate(classicDRPEntry.profile.toDate)}</p>
                </div>
                <p className="stucert-pending-msg">
                  Your DRP posting has ended. Waiting for your DRP Incharge to verify all sections and issue the certificate.
                </p>
                {!hasIncharge && (
                  <>
                    <p style={{ fontSize: 13, color: '#888', margin: '8px 0' }}>
                      No incharge assigned — you may also upload your certificate manually.
                    </p>
                    <CertUploadWidget
                      inputId="cert-current-pending"
                      uploading={certUploading === 'current'}
                      onUpload={(payload) => uploadCertificate('current', payload)}
                    />
                  </>
                )}
              </div>

            /* DRP in progress */
            ) : (
              <div className="stucert-card stucert-card-pending">
                <div className="stucert-card-status pending">
                  <FaTimesCircle /> Certificate Not Yet Issued
                </div>
                <div className="stucert-drp-info">
                  <p><strong>Hospital:</strong> {classicDRPEntry.profile.hospitalName}</p>
                  <p><strong>Period:</strong> {formatDate(classicDRPEntry.profile.fromDate)} – {formatDate(classicDRPEntry.profile.toDate)}</p>
                </div>
                {!hasIncharge ? (
                  <>
                    <p className="stucert-pending-msg">
                      No DRP Incharge has been assigned. You can upload your certificate manually.
                    </p>
                    <CertUploadWidget
                      inputId="cert-current-inprogress"
                      uploading={certUploading === 'current'}
                      onUpload={(payload) => uploadCertificate('current', payload)}
                    />
                  </>
                ) : (
                  <p className="stucert-pending-msg">
                    Your DRP certificate will be issued by your DRP Incharge once all sections (Attendance, Diary, Operations, Public Health) have been verified.
                  </p>
                )}
              </div>
            )}

            {/* ── Past DRP History ── */}
            {drpHistory.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={s.histHeader}>
                  <FaHistory style={{ color: '#6366f1', fontSize: 16 }} />
                  <span style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e' }}>Past DRPs</span>
                  <span style={{ marginLeft: 'auto', background: '#ede9fe', color: '#7c3aed', fontSize: 12, padding: '2px 10px', borderRadius: 12, fontWeight: 600 }}>
                    {drpHistory.length} past DRP{drpHistory.length > 1 ? 's' : ''}
                  </span>
                </div>
                <p style={{ fontSize: 13, color: '#666', margin: '6px 0 12px' }}>
                  Download system-generated certificates or upload certificates for DRPs without an incharge.
                </p>
                {[...drpHistory].reverse().map((hist, idx) => (
                  <HistoryDRPRow
                    key={hist._id}
                    hist={hist}
                    index={drpHistory.length - idx}
                    onUpload={(histId, payload) => uploadCertificate(histId, payload)}
                    uploading={certUploading}
                    onDownloadSystem={(h) => downloadSystemCert(h.profile || h)}
                    onDownloadUploaded={(histId) => downloadUploadedCert(histId)}
                    downloading={certDownloading}
                    onDelete={(histId) => deleteDrpCert(histId)}
                    deleting={drpCertDeleting}
                  />
                ))}
              </div>
            )}
          </section>
        )
      ) : (
        /* ── Advanced DRP ── */
        <section className="stucert-section">
          <div className="stucert-section-header">
            <FaHospital className="stucert-section-icon blue" />
            <h3>DRP Certificate</h3>
          </div>
          {drpPostings.length > 0 ? (
            drpPostings.map(posting => (
              <div key={posting._id} className="stucert-card stucert-card-issued">
                <div className="stucert-card-status issued">
                  <FaCheckCircle /> DRP Certificate Available
                </div>
                <div className="stucert-drp-info">
                  <p><strong>Hospital:</strong> {posting.hospitalName || '—'}</p>
                  <p><strong>District:</strong> {posting.district || '—'}</p>
                  <p><strong>Period:</strong> {formatDate(posting.startDate)} – {formatDate(posting.endDate)}</p>
                  {posting.certificateNumber && (
                    <p className="stucert-cert-number">Certificate No: {posting.certificateNumber}</p>
                  )}
                </div>
                <button className="stucert-download-btn stucert-download-btn-blue" onClick={() => handleDownloadDRP(posting)}>
                  <FaExternalLinkAlt /> View / Download DRP Certificate
                </button>
              </div>
            ))
          ) : (
            <div className="stucert-card stucert-card-pending">
              <div className="stucert-card-status pending">
                <FaTimesCircle /> No DRP Certificate
              </div>
              <p className="stucert-pending-msg">
                DRP certificates are generated automatically when your District Residency Programme posting is marked complete.
              </p>
            </div>
          )}
        </section>
      )}
      {/* ── Uploaded Certifications ─────────────────────────────────────────── */}
      <section className="stucert-section">
        <div className="stucert-section-header">
          <FaAward className="stucert-section-icon" style={{ color: '#7c3aed' }} />
          <h3>Other Certifications</h3>
        </div>
        <p style={{ fontSize: 13, color: '#666', margin: '-4px 0 16px' }}>
          Upload your certificates for Research Methodology, Ethics, Cardiac Life Support, and Poster/Paper Presentation.
        </p>

        {CERT_CONFIG.map((cfg) => {
          const meta = certifications?.[cfg.key];
          const isUploading = certUploadingType === cfg.key;
          const isDownloading = certDownloadingType === cfg.key;

          return (
            <div
              key={cfg.key}
              style={{
                border: `1px solid ${meta ? cfg.border : '#e5e7eb'}`,
                borderRadius: 12,
                padding: 16,
                marginBottom: 14,
                background: meta ? cfg.bg : '#fafafa'
              }}
            >
              {/* Title row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ fontSize: 18 }}>{cfg.icon}</span>
                    {cfg.label}
                  </div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 3 }}>{cfg.desc}</div>
                </div>
                {meta ? (
                  <span style={{ ...s.badgeUploaded, background: cfg.color }}>
                    <FaCheckCircle size={9} style={{ marginRight: 4 }} />Uploaded
                  </span>
                ) : (
                  <span style={s.badgeNone}>
                    <FaExclamationTriangle size={9} style={{ marginRight: 3 }} />Not Uploaded
                  </span>
                )}
              </div>

              {/* Uploaded file info + download */}
              {meta && (
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 12, color: '#555' }}>
                    {meta.mimeType === 'application/pdf'
                      ? <FaFilePdf style={{ color: '#dc2626', marginRight: 4 }} />
                      : <FaFileImage style={{ color: '#2563eb', marginRight: 4 }} />}
                    <strong>{meta.fileName}</strong>
                    {meta.uploadedAt && (
                      <span style={{ color: '#999', marginLeft: 8 }}>
                        Uploaded {formatDateShort(meta.uploadedAt)}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => downloadCertType(cfg.key)}
                    disabled={isDownloading || certDeletingType === cfg.key}
                    style={{ ...s.actionBtn, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
                  >
                    {isDownloading
                      ? <FaSpinner className="stucert-spinner-sm" style={{ marginRight: 6 }} />
                      : <FaDownload style={{ marginRight: 6 }} />}
                    Download
                  </button>
                  <button
                    onClick={() => deleteCertType(cfg.key, cfg.shortLabel)}
                    disabled={certDeletingType === cfg.key || isDownloading}
                    style={{ ...s.actionBtn, background: '#fff5f5', color: '#dc2626', border: '1px solid #fecaca' }}
                  >
                    {certDeletingType === cfg.key
                      ? <FaSpinner className="stucert-spinner-sm" style={{ marginRight: 6 }} />
                      : <FaTrash style={{ marginRight: 6 }} />}
                    Delete
                  </button>
                </div>
              )}

              {/* Upload widget — always available (allows re-upload) */}
              <div style={{ marginTop: meta ? 10 : 8 }}>
                {meta && (
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
                    Replace certificate:
                  </div>
                )}
                <CertUploadWidget
                  inputId={`cert-type-${cfg.key}`}
                  uploading={isUploading}
                  onUpload={(payload) => uploadCertType(cfg.key, payload)}
                />
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
};

// ─── Inline styles for new DRP elements ──────────────────────────────────────
const s = {
  subLabel: { fontSize: 12, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', margin: '4px 0 10px', padding: '6px 10px', background: '#f5f3ff', borderRadius: 8 },
  histHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '10px 12px', background: '#f5f3ff', borderRadius: 10 },
  histRow: { background: '#fafafa', borderRadius: 10, padding: 14, marginBottom: 10, display: 'flex', gap: 10, alignItems: 'flex-start' },
  histIndex: { fontSize: 11, padding: '3px 8px', borderRadius: 8, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 },
  badgeSystem: { fontSize: 10, padding: '2px 7px', borderRadius: 8, background: '#d97706', color: '#fff', fontWeight: 600, display: 'inline-flex', alignItems: 'center' },
  badgeUploaded: { fontSize: 10, padding: '2px 7px', borderRadius: 8, background: '#2563eb', color: '#fff', fontWeight: 600, display: 'inline-flex', alignItems: 'center' },
  badgeNone: { fontSize: 10, padding: '2px 7px', borderRadius: 8, background: '#fee2e2', color: '#dc2626', fontWeight: 600, display: 'inline-flex', alignItems: 'center' },
  actionBtn: { fontSize: 12, padding: '6px 12px', borderRadius: 8, background: '#fff7ed', color: '#b45309', border: '1px solid #fed7aa', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', fontWeight: 600 },
  expandBtn: { fontSize: 12, padding: '5px 10px', borderRadius: 8, background: '#f3f4f6', color: '#555', border: '1px solid #e5e7eb', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', fontWeight: 500 }
};

export default StudentCertificates;
